import {DatePipe} from '@angular/common';
import {Component, HostListener, Injectable, OnInit} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {CsvMachineData} from 'src/app/model/custom/import-csv/csv-machine-data';
import {CsvMachineErrorList} from 'src/app/model/custom/import-csv/csv-machine-error';
import {MachineModel} from 'src/app/model/machine-model';
import {OrganizationSummary} from 'src/app/model/organization';
import {CommonMessagesService} from 'src/app/services/common-messages.service';
import {MachineModelsService} from 'src/app/services/machine-models.service';
import {ComponentCanDeactivate} from "../../common/can-deactivate/component-can-deactivate";
import {ImportCsvMachineErrors} from './import-csv-machine-errors';
import {MachinePropertiesErrors} from './machine-properties-errors';
import {AppConfig} from "../../shared/global-variables";
import {HttpResponse} from '@angular/common/http';
import {MachinesService} from "../../services/machines.service";
import {FileData, FileErrors} from 'src/app/model/custom/import-csv/file-data';
import {MachinePersistRequestCsv} from "../../model/requests/machine-persist-request-csv";
import {MachinePersistRequest} from "../../model/requests/machine-persist-request";
import * as moment from "moment";
import {OrganizationsService} from "../../services/organizations.service";
import {TimezoneService} from "../../services/timezone.service";
import {FileUploadData} from "../../model/custom/import-csv/file-upload-data";
import {AuditsService} from "../../services/audits.service";
import {ImportFileType} from "../../model/import-file-type";
import {FileExtensionType} from "../../model/file-extension-type";

@Component({
  selector: 'app-import-csv',
  templateUrl: './import-csv.component.html',
  styleUrls: ['./import-csv.component.scss']
})
@Injectable({
  providedIn: 'root'
})
export class ImportCsvComponent implements OnInit, ComponentCanDeactivate {

  public machinePropertiesErrors: MachinePropertiesErrors = new MachinePropertiesErrors(this.i18n);

  public loadedData: boolean = false;
  public infoExpanded: boolean = true;

  public files: FileData[] = [];
  public machines: CsvMachineData[] = [];
  public filesUploaded: boolean = false;
  public machinesCreated: boolean = false;
  public creatingMachines: boolean = false;

  public rows: number = 10;
  public first: number = 0;
  public totalMachines: number;

  private readonly SUCCESS_STATUS_CODE: number = 200;
  private readonly CONFLICT_STATUS_CODE: number = 409;

  private fileFormatIncorrect: boolean = false;

  private organizationsByUuid: { [key: string]: OrganizationSummary };
  private organizationsByCode: { [key: string]: OrganizationSummary };
  private machineModelsByUuid: { [key: string]: MachineModel };
  private machineModelsByName: { [key: string]: MachineModel };


  constructor(
    private readonly machinesService: MachinesService,
    private readonly organizationsService: OrganizationsService,
    private readonly machineModelsService: MachineModelsService,
    private readonly commonMessageService: CommonMessagesService,
    private readonly i18n: TranslocoService,
    private readonly datePipe: DatePipe,
    private readonly timezoneService: TimezoneService,
    private readonly auditsService: AuditsService,
    private readonly commonMessagesService: CommonMessagesService
  ) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    const promises = [
      this.organizationsService.getOrganizationsSummary().subscribe(response => {
        this.organizationsByUuid = response.organizationsByUuid;
        this.organizationsByCode = response.organizationsByCode;
      }),
      this.machineModelsService.getModels().subscribe(response => {
        this.buildMachineModelsJson(response);
      })
    ];
    Promise.all(promises).then(() => {
      this.loadedData = true;
    });
  }

  private buildMachineModelsJson(machineModelResponse: any): void {
    [this.machineModelsByName, this.machineModelsByUuid] = machineModelResponse.models.reduce((acc, value) => {
      acc[0][value.modelName] = value;
      acc[1][value.machineModelUuid] = value;
      return acc;
    }, [{}, {}])
  }

  public async processFiles(files: File[]): Promise<void> {
    const filePromises: Promise<FileData>[] = files.map((file, index) => {
      const fileIdentifier: string = 'id-' + index + '-' + (new Date()).getTime();
      return this.readFile(fileIdentifier, file)
    });

    const _files: FileData[] = await Promise.all(filePromises);

    this.files = this.files.concat(_files);
    this.filesUploaded = true;
    this.infoExpanded = false;
  }

  private readFile(fileIdentifier: string, file: File): Promise<FileData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const fileReader: string[] = reader.result.toString().split('\n');
          const fileErrors: FileErrors = this.processFileMachines(fileIdentifier, fileReader);
          const fileData: FileData = {
            fileIdentifier,
            file,
            fileErrors
          };
          resolve(fileData);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsText(file, 'ISO-8859-3');
      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  private processFileMachines(fileIdentifier: string, file: string[]): FileErrors | null {
    let machines: CsvMachineData[] = [];
    let fileErrors: FileErrors = {
      lines: [],
    };
    file.forEach((line: string, lineIndex: number): void => {
      if (line.trim() !== '') {
        const rawMachineData: string[] = line.trim().split(';');
        if (this.validateNumberOfParams(rawMachineData)) {
          const machine: MachinePersistRequestCsv = this.getMachinePersistRequestCsv(rawMachineData);
          let csvMachineData: CsvMachineData = this.getCsvMachineData(fileIdentifier, machine, rawMachineData, lineIndex);

          const errors: CsvMachineErrorList = this.validateMachineData(csvMachineData);
          if (errors != null) {
            csvMachineData.status = 'ERROR';
            csvMachineData.errors = errors;
          }

          machines.push(csvMachineData);
        } else {
          fileErrors.lines.push(lineIndex);
        }
      }
    });
    this.fileFormatIncorrect = false;
    if (machines.length > 0) {
      this.machines = this.machines.concat(machines);
    }
    this.totalMachines = this.machines.length;
    return fileErrors.lines.length > 0 ? fileErrors : null;
  }

  private getCsvMachineData(fileIdentifier: string, machine: MachinePersistRequestCsv, rawMachineData: string[],
                            lineIndex: number): CsvMachineData {
    return {
      machine,
      machineIdentifier: fileIdentifier + '-' + lineIndex,
      fileIdentifier,
      organization: this.organizationsByUuid[machine.organizationUuid] ? this.organizationsByUuid[machine.organizationUuid] : null,
      machineModel: this.machineModelsByUuid[machine.machineModelUuid] ? this.machineModelsByUuid[machine.machineModelUuid] : null,
      machineModelName: rawMachineData[6],
      status: 'NONE'
    };
  }

  private validateNumberOfParams(rawMachineData: string[]): boolean {
    return rawMachineData.length === 14;
  }

  public isValidDate(date: string): boolean {
    const nameRegexp: RegExp = /^\d+$/;
    return !(date == null || !nameRegexp.test(date) || !moment(Number.parseInt(date)).isValid());
  }

  private getMachinePersistRequestCsv(rawMachineData: string[]): MachinePersistRequestCsv {
    const [serialNumber, alias, description, simNumber, latitude, longitude, eui, machineModelName, orgCode, parentCode, automaticGeoloc,
      status, startDate, endDate] = rawMachineData;
    let uniqueCode: string = (orgCode && parentCode) ? orgCode + "." + parentCode : '';
    return {
      alias,
      serial: serialNumber,
      description,
      longitude,
      latitude,
      eui,
      orgCode,
      parentCode,
      organizationUuid: this.organizationsByCode[uniqueCode] ? this.organizationsByCode[uniqueCode].organizationUuid : null,
      machineModelUuid: this.machineModelsByName[machineModelName] ? this.machineModelsByName[machineModelName].machineModelUuid : null,
      automaticGeoloc,
      status,
      startDate,
      endDate,
      simNumber
    };
  }

  private getMachinePersistRequest(machineDataCsv: MachinePersistRequestCsv): MachinePersistRequest {
    let periodRange = {from: null, until: null};
    if (machineDataCsv.startDate != null && machineDataCsv.startDate.trim().length > 0 &&
      machineDataCsv.endDate != null && machineDataCsv.endDate.trim().length > 0) {
      periodRange = this.timezoneService.timestampRangeFromCalendarData([
        moment(machineDataCsv.startDate, "YYYY-MM-DD").toDate(),
        moment(machineDataCsv.endDate, "YYYY-MM-DD").toDate()
      ]);
    }
    return {
      machineUuid: null,
      alias: machineDataCsv.alias,
      serial: machineDataCsv.serial,
      description: machineDataCsv.description,
      longitude: Number.parseFloat(machineDataCsv.longitude),
      latitude: Number.parseFloat(machineDataCsv.latitude),
      eui: machineDataCsv.eui,
      organizationUuid: machineDataCsv.organizationUuid,
      machineModelUuid: machineDataCsv.machineModelUuid,
      active: (machineDataCsv.status === 'ACTIVE'),
      automaticGeoloc: (machineDataCsv.automaticGeoloc === 'yes'),
      startDate: periodRange.from,
      endDate: periodRange.until,
      machineGroupsUuids: [],
      simNumber: machineDataCsv.simNumber
    };
  }

  private validateMachineData(csvMachineData: CsvMachineData): CsvMachineErrorList | null {
    const machine: MachinePersistRequestCsv = csvMachineData.machine;
    const importCsvMachineErrors: ImportCsvMachineErrors = new ImportCsvMachineErrors(this.i18n, this.datePipe);

    this.validateSerialNumber(machine, importCsvMachineErrors);
    this.validateAlias(machine, importCsvMachineErrors);
    this.validateDescription(machine, importCsvMachineErrors);
    this.validateLatitude(machine, importCsvMachineErrors);
    this.validateLongitude(machine, importCsvMachineErrors);
    this.validateOrganization(machine, importCsvMachineErrors);
    this.validateMachineModel(csvMachineData, importCsvMachineErrors);
    this.validateEui(machine, importCsvMachineErrors);
    this.validateAutomaticGeoloc(machine, importCsvMachineErrors);
    this.validateSubscription(machine, importCsvMachineErrors);
    this.validateSimNumber(machine, importCsvMachineErrors);

    return Object.entries(importCsvMachineErrors.errors).length > 0 ? importCsvMachineErrors.errors : null;
  }

  private validateSerialNumber(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'serial')) {
      importCsvMachineErrors.noSpecialCharacters(machine, 'serial');
      importCsvMachineErrors.noWhitespaces(machine, 'serial');
      importCsvMachineErrors.minLength(machine, 'serial', AppConfig.FORM_IDENTIFIER_MIN_LENGTH);
      importCsvMachineErrors.maxLength(machine, 'serial', 255);
    }
  }

  private validateAlias(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'alias')) {
      importCsvMachineErrors.maxLength(machine, 'alias', 255);
    }
  }

  private validateDescription(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (machine.description != null && machine.description.trim().length > 0) {
      importCsvMachineErrors.maxLength(machine, 'description', 255);
    }
  }

  private validateSimNumber(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (machine.simNumber != null && machine.simNumber.trim().length > 0) {
      importCsvMachineErrors.maxLength(machine, 'simNumber', 25);
    }
  }

  private validateLatitude(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'latitude') &&
      importCsvMachineErrors.isFloatNumber(machine, 'latitude')) {
      importCsvMachineErrors.validLatitude(machine);
    }
  }

  private validateLongitude(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'longitude') &&
      importCsvMachineErrors.isFloatNumber(machine, 'longitude')) {
      importCsvMachineErrors.validLongitude(machine);
    }
  }

  private validateOrganization(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'orgCode') &&
      importCsvMachineErrors.required(machine, 'parentCode')) {
      importCsvMachineErrors.foundOrganization<OrganizationSummary>(machine, this.organizationsByCode);
    }
  }

  private validateMachineModel(csvMachineData: CsvMachineData, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(csvMachineData, 'machineModelName', 'machineModelUuid')) {
      importCsvMachineErrors.foundInData<MachineModel>(csvMachineData.machine, 'machineModelUuid', this.machineModelsByUuid);
    }
  }

  private validateEui(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'eui')) {
      importCsvMachineErrors.length(machine, 'eui', 16);
      importCsvMachineErrors.noSpecialCharacters(machine, 'eui');
    }
  }

  private validateAutomaticGeoloc(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'automaticGeoloc')) {
      importCsvMachineErrors.validValues(machine, 'automaticGeoloc', ['yes', 'no']);
    }
  }

  private validateSubscription(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): void {
    if (importCsvMachineErrors.required(machine, 'status')) {
      importCsvMachineErrors.validValues(machine, 'status', ['ACTIVE', 'INACTIVE']);
      if (machine['status'] === 'ACTIVE') {
        if (this.validateStartDate(machine, importCsvMachineErrors) && this.validateEndDate(machine, importCsvMachineErrors)) {
          importCsvMachineErrors.subscriptionDatesRange(machine);
        }
      } else if (machine['status'] === 'INACTIVE') {
        importCsvMachineErrors.notRequired(machine, 'startDate');
        importCsvMachineErrors.notRequired(machine, 'endDate');
      }
    }
  }

  private validateStartDate(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): boolean {
    if (importCsvMachineErrors.required(machine, 'startDate') &&
      importCsvMachineErrors.validDate(machine, 'startDate')) {
      importCsvMachineErrors.dateInLastNYears(machine, 'startDate', 10);
      return true;
    }
    return false;
  }

  private validateEndDate(machine: MachinePersistRequestCsv, importCsvMachineErrors: ImportCsvMachineErrors): boolean {
    return importCsvMachineErrors.required(machine, 'endDate') &&
      importCsvMachineErrors.validDate(machine, 'endDate');
  }

  public deleteAttachment(index: number): void {
    const fileIdentifier: string = this.files[index]['fileIdentifier'];
    this.files.splice(index, 1);
    if (this.files.length == 0) {
      this.filesUploaded = false;
      this.infoExpanded = true;
    }
    this.machines = this.machines.filter((machine: CsvMachineData) => machine.fileIdentifier != fileIdentifier);
    this.totalMachines = this.machines.length;
  }

  public deleteMachine(machineIdentifier: string): void {
    this.machines = this.machines.filter((machine: CsvMachineData) => machine.machineIdentifier != machineIdentifier);
    this.totalMachines = this.machines.length;
  }

  public importMachines(): void {
    const wellFormedMachines: CsvMachineData[] = this.machines.filter(m => m.errors == null).map(m => {
      m.status = 'UPLOADING';
      return m;
    });
    if (wellFormedMachines.length == 0) {
      this.commonMessageService.showSuccessfullyCreatedMessage();
      return;
    }

    let fileIdentifiers: Set<string> = new Set<string>();
    const machinesSubscriptions = wellFormedMachines.reduce((acc, value) => {
      acc[value.machineIdentifier] = this.machinesService.createMachine(this.getMachinePersistRequest(value.machine))
        .pipe(map(response => response), catchError(error => of(error)));
      fileIdentifiers.add(value.fileIdentifier);
      return acc;
    }, {});

    this.creatingMachines = true;
    forkJoin(machinesSubscriptions).subscribe((response) => {
      this.machinesCreated = true;
      Object.entries(response).forEach(entry => {
        const machineIndex: number = this.machines.findIndex(machine => machine.machineIdentifier === entry[0]);
        if ((entry[1] as HttpResponse<any>).status === this.SUCCESS_STATUS_CODE) {
          this.machines[machineIndex].status = 'UPLOADED';

        } else if ((entry[1] as HttpResponse<any>).status === this.CONFLICT_STATUS_CODE) {
          this.machines[machineIndex].status = 'ERROR';
          this.machines[machineIndex].errors = {upload: [{message: 'serialNumberAlreadyExists', params: {}}]};

        } else {
          this.machines[machineIndex].status = 'ERROR';
          this.machines[machineIndex].errors = {upload: [{message: 'unexpectedError', params: {}}]};
        }
      });

      this.addUploadAudits(fileIdentifiers);
      this.commonMessageService.showSuccessfullyCreatedMessage();
      this.creatingMachines = false;
    });
  }

  private addUploadAudits(fileIdentifiers: Set<string>): void {
    const filesToUpload: FileUploadData[] = this.buildFilesToUpload(fileIdentifiers);
    this.auditsService.createUploadAudit(filesToUpload, ImportFileType.IMPORT_MACHINES, FileExtensionType.CSV).subscribe();
  }

  private buildFilesToUpload(fileIdentifiers: Set<string>): FileUploadData[] {
    return this.files
      .filter((fileData: FileData) => fileIdentifiers.has(fileData.fileIdentifier))
      .map((fileData: FileData) => {
        return {
          fileName: fileData.file.name,
          fileSize: fileData.file.size
        }
      });
  }

  public cancel(): void {
    this.commonMessagesService.showCancelRequestConfirmation(() => {
      this.refresh();
    });
  }

  private refresh(): void {
    this.machinePropertiesErrors = new MachinePropertiesErrors(this.i18n);
    this.files = [];
    this.machines = [];
    this.totalMachines = null;
    this.infoExpanded = true;
    this.filesUploaded = false;
    this.machinesCreated = false;
    this.creatingMachines = false;
    this.fileFormatIncorrect = false;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.files || this.files.length === 0 || this.machinesCreated;
  }

}
